import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Ban, ExternalLink, Inbox } from "lucide-react";
import { Evidence, Facts, FitBadge } from "./bits";

// Section 5: qualified + check-me opportunities wait here. Promote mints the project number; nothing reaches the pipeline on its own.
export function TrayList({ onChanged }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/bd/tray").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const promote = async (t) => {
    try {
      const r = await api.post(`/bd/tray/${t.tray_id}/promote`, {});
      toast.success(`Promoted → ${r.data.project_number}`);
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not promote"); }
  };
  const screen = async (t) => {
    const reason = window.prompt("Why is this not for us? (becomes a correction the judge learns from)");
    if (!reason || !reason.trim()) return;
    try { await api.post(`/bd/tray/${t.tray_id}/screen-out`, { reason: reason.trim() }); toast.success("Screened out"); load(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not screen out"); }
  };

  if (rows === null) return <div className="text-sm text-slate-400 py-12 text-center">Loading…</div>;
  if (!rows.length) return (
    <Card className="p-12 text-center" data-testid="bd-tray-empty">
      <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-3" />
      <div className="text-sm font-medium text-slate-600">Nothing new from email.</div>
      <div className="text-xs text-slate-400 mt-1">Qualified and check-me opportunities land here; you promote them to the pipeline with one click.</div>
    </Card>
  );
  return (
    <div className="space-y-3" data-testid="bd-tray-list">
      {rows.map((t) => (
        <Card key={t.tray_id} className={`p-4 ${t.check_me ? "border-amber-200" : "border-emerald-200"}`} data-testid={`bd-tray-${t.tray_id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <FitBadge fit={t.fit} testid={`bd-tray-fit-${t.tray_id}`} />
                {t.new_project ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-800 border border-sky-200" title={t.new_project.why} data-testid={`bd-tray-new-project-${t.tray_id}`}>{t.new_project.flag}</span> : null}
                {t.new_scope ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200" data-testid={`bd-tray-new-scope-${t.tray_id}`}>{t.new_scope.flag}</span>
                  : t.existing_client && <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-800 border border-sky-200">existing client — new project?</span>}
                {t.platform && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border">{t.platform}</span>}
                <span className="text-xs text-slate-400">{fmtDate(t.date)}</span>
              </div>
              <div className="text-sm font-semibold text-black mt-1">{t.project_name || t.subject || "Untitled"}{t.company ? <span className="text-slate-500 font-normal"> · {t.company}</span> : null}</div>
              <div className="text-xs text-slate-600 mt-1" data-testid={`bd-tray-reason-${t.tray_id}`}>{t.fit_reason}</div>
              {t.asks && <div className="text-xs text-slate-700 mt-1"><span className="text-slate-400">Ask:</span> {t.asks}</div>}
              <Facts facts={t.facts} />
              <Evidence items={t.evidence} testid={`bd-tray-evidence-${t.tray_id}`} />
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <Button size="sm" onClick={() => promote(t)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`bd-tray-promote-${t.tray_id}`}><ArrowUpRight className="w-3.5 h-3.5 mr-1" /> Promote to pipeline</Button>
              <Button size="sm" variant="outline" onClick={() => screen(t)} data-testid={`bd-tray-screen-${t.tray_id}`}><Ban className="w-3.5 h-3.5 mr-1" /> Screen out</Button>
              <a href={t.gmail_link} target="_blank" rel="noreferrer" className="text-xs text-[#003E87] hover:underline inline-flex items-center gap-1 justify-center" data-testid={`bd-tray-email-${t.tray_id}`}><ExternalLink className="w-3 h-3" /> Open email</a>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
